/**
 * EMPRESA, USUARIOS Y PERFILES — la pantalla
 *
 * Los tres bloques van juntos porque son una sola pregunta: quién puede hacer
 * qué, y en qué empresa. Separarlos obliga a saltar entre pantallas para
 * contestarla, y las reglas que los unen viven justo en el cruce —un usuario
 * nuevo necesita una empresa activa; la última administradora no se puede dar
 * de baja; un administrador de empresa no toca a uno de plataforma—.
 *
 * ═══ POR QUÉ HAY UN SELECTOR DE PERFIL ═══
 *
 * Todavía no hay entrada: `server/identidad.js` sabe comprobar una contraseña,
 * pero nadie lo llama y no hay sesiones —eso es la e132—. Una pantalla de
 * permisos sin identidad no se puede ver funcionar: o muestra todo, o muestra
 * nada, según lo que se dé por sentado, y en los dos casos parece que anda.
 *
 * Así que acá se ELIGE con qué perfil mirar. Es una herramienta para ver el
 * modelo, NO una autenticación, y por eso:
 *
 *   · está dicho en la pantalla, no escondido en un comentario;
 *   · NO se guarda: cada vez que se abre la aplicación vuelve al perfil por
 *     defecto. Un perfil «elegido» que sobrevive al refresco es exactamente lo
 *     que un día va a parecer una sesión, y nadie lo va a revisar.
 *
 * ═══ LO QUE NO HACE ═══
 *
 * No guarda contraseñas. Revisa que la clave cumpla el mínimo para no crear un
 * usuario cuya clave el servidor va a rechazar después, y no la retiene: el
 * hash lo hace `server/identidad.js` con bcrypt cuando exista la entrada.
 * Guardar un secreto en el navegador es una decisión que no se toma de paso.
 */
import { useEffect, useRef, useState } from "react";

import {
  PERFILES,
  PERMISOS,
  darDeBaja,
  darDeBajaEmpresa,
  esDePlataforma,
  esUltimoAdministradorDeEmpresa,
  historialDe,
  identidadInicial,
  nombreDePerfil,
  nuevaEmpresa,
  nuevoUsuario,
  puede,
  resumenDeEmpresa,
  reactivar,
  revisarAlta,
  revisarBaja,
  revisarBajaDeEmpresa,
  revisarCambioDePerfil,
  revisarClave,
  revisarEmpresa,
  cambiarPerfil,
  usuariosDeEmpresa,
} from "../../domain/identidad.js";
import {
  clientesDe,
  darDeBajaCliente,
  nuevoCliente,
  reactivarCliente,
  revisarCliente,
} from "../../domain/clientes.js";
import "./administracion.css";

export default function PanelAdministracion({
  estado,
  alCambiar,
  clientes = [],
  alCambiarClientes,
  perfilQueMira = "company_admin",
  alCambiarPerfil,
  ahora = new Date(),
  /* ═══ LA ADMINISTRACIÓN REAL, POR LA API ═══
     Cuando `VistaDeGestion` pasa estos manejadores, la pantalla escribe en la
     base y un usuario creado acá PUEDE ENTRAR. Sin ellos —la vista de
     operación sin sesión, y las pruebas de la pantalla— queda el
     comportamiento local, con el aviso de que no crea cuentas. */
  alCrearUsuario = null,
  alActualizarUsuario = null,
  alCrearEmpresa = null,
  alActualizarEmpresa = null,
}) {
  const [mensaje, setMensaje] = useState(null);
  const [borrador, setBorrador] = useState({ nombre: "", email: "", perfil: "pilot", clave: "" });
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [editando, setEditando] = useState(null);
  const [clienteNuevo, setClienteNuevo] = useState({ nombre: "", contacto: "", email: "" });

  /* ═══ EL RECHAZO TIENE QUE VERSE DONDE SE APRETÓ ═══

     El aviso vive arriba del todo, y los botones de la tabla pueden quedar tres
     pantallas más abajo: en un iPad el rechazo aparecía fuera de la vista y el
     botón parecía no hacer nada. Con el aviso pegado arriba y traído a la vista,
     la regla se lee donde se chocó con ella. */
  const avisoRef = useRef(null);
  useEffect(() => {
    /* Con `?.` en la llamada: jsdom no implementa `scrollIntoView`, y sin eso la
       prueba revienta con un error que no tiene nada que ver con lo que mide. */
    if (mensaje) avisoRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [mensaje]);

  const base = estado || identidadInicial();
  const empresas = base.empresas || [];
  const usuarios = base.usuarios || [];
  const activa = empresas.find((e) => e.id === base.empresaActiva) || empresas[0] || null;

  /* Quién mira: el perfil elegido. El identificador lleva el prefijo `perfil:`
     para que en el historial se lea que la acción no la hizo una persona
     identificada — cuando exista la entrada, acá va el usuario y esta línea se
     borra sola. */
  const quienMira = {
    id: `perfil:${perfilQueMira}`,
    perfil: perfilQueMira,
    empresaId: activa?.id ?? null,
  };

  const deLaEmpresa = usuariosDeEmpresa(usuarios, activa?.id);
  const resumen = activa ? resumenDeEmpresa(activa, usuarios) : null;

  /* Los clientes de la empresa activa, CON los dados de baja a la vista. Un
     cliente que desaparece de la lista hace pensar que se borró, y no es eso lo
     que pasó ni lo que se debe hacer: la baja dice «no se elige más», no «no
     existió». */
  const clientesDeLaEmpresa = clientesDe(clientes, activa?.id, { incluirBajas: true });

  const ver = (tipo, texto) => setMensaje({ tipo, texto });
  const guardar = (cambios) => alCambiar({ ...base, ...cambios });

  const apiUsuarios = typeof alCrearUsuario === "function" && typeof alActualizarUsuario === "function";
  const apiEmpresas = typeof alCrearEmpresa === "function" && typeof alActualizarEmpresa === "function";

  /* Un manejador de la API devuelve `{ ok, usuario|empresa, motivo }`. La
     pantalla no inventa el motivo: muestra el que escribió el servidor. */
  const respuesta = (r) => {
    if (!r?.ok) {
      ver("problema", r?.motivo || "No se pudo completar la operación.");
      return null;
    }
    return r;
  };

  /* ── usuarios ─────────────────────────────────────────────────────────── */

  const agregarUsuario = async (evento) => {
    evento.preventDefault();
    const datos = { ...borrador, empresaId: activa?.id };

    const problema = revisarAlta(datos, { usuarios, empresas, quien: quienMira });
    if (problema) return ver("problema", problema);

    const problemaClave = revisarClave(borrador.clave, { email: borrador.email });
    if (problemaClave) return ver("problema", problemaClave);

    if (apiUsuarios) {
      const r = respuesta(await alCrearUsuario(datos));
      if (!r) return null;
      guardar({ usuarios: [...usuarios, r.usuario] });
      setBorrador({ nombre: "", email: "", perfil: "pilot", clave: "" });
      return ver("ok", `Alta de ${r.usuario.nombre}. Ya puede entrar con su correo y su contraseña.`);
    }

    const usuario = nuevoUsuario({ ...datos, quien: quienMira.id, ahora });
    guardar({ usuarios: [...usuarios, usuario] });
    setBorrador({ nombre: "", email: "", perfil: "pilot", clave: "" });
    return ver("ok", `Alta de ${usuario.nombre}. Queda como invitado: esta pantalla no está conectada a la base y todavía no puede entrar.`);
  };

  const alternarUsuario = async (usuario) => {
    const deBaja = usuario.activo;
    const problema = deBaja
      ? revisarBaja(usuario, { usuarios, quien: quienMira })
      : null;
    if (problema) return ver("problema", problema);

    if (apiUsuarios) {
      const r = respuesta(await alActualizarUsuario(usuario.id, { activo: !deBaja }));
      if (!r) return null;
      guardar({ usuarios: usuarios.map((u) => (u.id === usuario.id ? r.usuario : u)) });
      return ver("ok", deBaja ? `${usuario.nombre} queda de baja.` : `${usuario.nombre} vuelve a estar activo.`);
    }

    const cambios = usuarios.map((u) => {
      if (u.id !== usuario.id) return u;
      const quien = quienMira.id;
      return deBaja ? darDeBaja(u, { quien, ahora }) : reactivar(u, { quien, ahora });
    });
    guardar({ usuarios: cambios });
    return ver("ok", deBaja ? `${usuario.nombre} queda de baja.` : `${usuario.nombre} vuelve a estar activo.`);
  };

  const cambiarElPerfil = async (usuario, perfil) => {
    const problema = revisarCambioDePerfil(usuario, perfil, { usuarios, quien: quienMira });
    if (problema) return ver("problema", problema);

    if (apiUsuarios) {
      const r = respuesta(await alActualizarUsuario(usuario.id, { perfil }));
      if (!r) return null;
      guardar({ usuarios: usuarios.map((u) => (u.id === usuario.id ? r.usuario : u)) });
      return ver("ok", `${usuario.nombre} pasa a ${nombreDePerfil(perfil)}.`);
    }

    guardar({
      usuarios: usuarios.map((u) => (u.id === usuario.id ? cambiarPerfil(u, perfil, { quien: quienMira.id, ahora }) : u)),
    });
    return ver("ok", `${usuario.nombre} pasa a ${nombreDePerfil(perfil)}.`);
  };

  /* ── empresas ─────────────────────────────────────────────────────────── */

  const agregarEmpresa = async (evento) => {
    evento.preventDefault();
    const problema = revisarEmpresa({ nombre: nombreNuevo }, { empresas, quien: quienMira });
    if (problema) return ver("problema", problema);

    if (apiEmpresas) {
      const r = respuesta(await alCrearEmpresa(nombreNuevo));
      if (!r) return null;
      guardar({ empresas: [...empresas, r.empresa], empresaActiva: r.empresa.id });
      setNombreNuevo("");
      return ver("ok", `Empresa ${r.empresa.nombre} creada.`);
    }

    const empresa = nuevaEmpresa({ nombre: nombreNuevo, quien: quienMira.id, ahora });
    guardar({ empresas: [...empresas, empresa], empresaActiva: empresa.id });
    setNombreNuevo("");
    return ver("ok", `Empresa ${empresa.nombre} creada.`);
  };

  const guardarNombre = async (evento) => {
    evento.preventDefault();
    const problema = revisarEmpresa({ nombre: editando }, { empresas, editando: activa.id, quien: quienMira });
    if (problema) return ver("problema", problema);
    const nombre = editando.trim();

    if (apiEmpresas) {
      const r = respuesta(await alActualizarEmpresa(activa.id, { nombre }));
      if (!r) return null;
      guardar({ empresas: empresas.map((e) => (e.id === activa.id ? r.empresa : e)) });
      setEditando(null);
      return ver("ok", "Nombre de la empresa actualizado.");
    }

    guardar({
      empresas: empresas.map((e) => (e.id === activa.id ? { ...e, nombre, slug: nuevaEmpresa({ nombre }).slug } : e)),
    });
    setEditando(null);
    return ver("ok", "Nombre de la empresa actualizado.");
  };

  const darDeBajaLaEmpresa = async () => {
    const problema = revisarBajaDeEmpresa(activa, { usuarios, quien: quienMira });
    if (problema) return ver("problema", problema);

    if (apiEmpresas) {
      const r = respuesta(await alActualizarEmpresa(activa.id, { activo: false }));
      if (!r) return null;
      guardar({ empresas: empresas.map((e) => (e.id === activa.id ? r.empresa : e)) });
      return ver("ok", `Empresa ${activa.nombre} dada de baja.`);
    }

    guardar({
      empresas: empresas.map((e) => (e.id === activa.id ? darDeBajaEmpresa(e, { quien: quienMira.id, ahora }) : e)),
    });
    return ver("ok", `Empresa ${activa.nombre} dada de baja.`);
  };

  /* ── clientes ────────────────────────────────────────────────────────── */

  const agregarCliente = (evento) => {
    evento.preventDefault();
    const datos = { ...clienteNuevo, empresaId: activa?.id };

    /* El motivo lo arma el dominio y se muestra tal cual, como todo lo demás de
       esta pantalla. Se revisa ANTES de crear y no después de mostrar el aviso:
       un cliente rechazado que igual entró a la lista deja el aviso mintiendo. */
    const problema = revisarCliente(datos, { clientes, empresas, quien: quienMira });
    if (problema) return ver("problema", problema);

    const cliente = nuevoCliente({ ...datos, quien: quienMira.id, ahora });
    alCambiarClientes([...clientes, cliente]);
    setClienteNuevo({ nombre: "", contacto: "", email: "" });
    return ver("ok", `Cliente ${cliente.nombre} creado.`);
  };

  const alternarCliente = (cliente) => {
    alCambiarClientes(clientes.map((c) => {
      if (c.id !== cliente.id) return c;
      return c.activo
        ? darDeBajaCliente(c, { quien: quienMira.id, ahora })
        : reactivarCliente(c, { quien: quienMira.id, ahora });
    }));
    return ver("ok", cliente.activo
      ? `Cliente ${cliente.nombre} dado de baja. Sus horas ya voladas siguen contando.`
      : `Cliente ${cliente.nombre} reactivado.`);
  };

  /* ── lo que el perfil puede ver ───────────────────────────────────────── */

  const veEmpresa = puede(perfilQueMira, "empresa.leer");
  const editaEmpresa = puede(perfilQueMira, "empresa.escribir");
  const creaEmpresas = puede(perfilQueMira, "empresas.administrar");
  const veUsuarios = puede(perfilQueMira, "usuarios.leer");
  const escribeUsuarios = puede(perfilQueMira, "usuarios.escribir");
  const vePerfiles = puede(perfilQueMira, "perfiles.leer");

  return (
    <div className="adm">
      <header className="adm__entrada">
        <div className="adm__entrada-texto">
          <h2 className="adm__titulo">Empresa, usuarios y perfiles</h2>
          <p className="adm__nota">
            {typeof alCambiarPerfil === "function"
              ? "Todavía no hay entrada, así que la pantalla te deja elegir con qué perfil miras. Es para ver el modelo: lo que un perfil no puede hacer queda a la vista, con el motivo."
              : "El perfil con el que ves esta pantalla es el de tu sesión. Lo que tu perfil no puede hacer queda a la vista, con el motivo."}
          </p>
        </div>
        {typeof alCambiarPerfil === "function" && (
          <div className="adm__perfiles" role="group" aria-label="Mirar la pantalla como">
            {PERFILES.map((p) => (
              <button
                key={p.clave}
                type="button"
                className={`adm__perfil${p.clave === perfilQueMira ? " es-actual" : ""}`}
                aria-pressed={p.clave === perfilQueMira}
                onClick={() => {
                  setMensaje(null);
                  alCambiarPerfil(p.clave);
                }}
              >
                {p.nombre}
              </button>
            ))}
          </div>
        )}
      </header>

      {mensaje && (
        <p ref={avisoRef} className={`adm__mensaje adm__mensaje--${mensaje.tipo}`} role="status">
          {mensaje.texto}
        </p>
      )}

      {/* ═══ EMPRESA ═══ */}
      <section className="adm__seccion" aria-labelledby="adm-empresa">
        <h3 className="adm__subtitulo" id="adm-empresa">Empresa</h3>

        {!veEmpresa ? (
          <p className="adm__bloqueo">
            {nombreDePerfil(perfilQueMira)} no ve los datos de la empresa: le falta el permiso «empresa.leer».
          </p>
        ) : (
          <>
            {/* El selector de empresa es del administrador de plataforma: es el
                único que mira varias. El de empresa está fijado a la suya, y esa
                fijación es la regla de aislamiento, no una comodidad. */}
            {creaEmpresas && empresas.length > 1 && (
              <label className="adm__campo">
                <span>Mirando la empresa</span>
                <select
                  value={activa?.id || ""}
                  onChange={(e) => {
                    setMensaje(null);
                    guardar({ empresaActiva: e.target.value });
                  }}
                >
                  {empresas.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre}{e.activo ? "" : " (de baja)"}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {activa ? (
              <div className="adm__tarjeta">
                {editando === null ? (
                  <div className="adm__tarjeta-fila">
                    <strong>{activa.nombre}</strong>
                    <code className="adm__slug">{activa.slug}</code>
                    <span className={`adm__estado${activa.activo ? "" : " adm__estado--baja"}`}>
                      {activa.activo ? "activa" : "de baja"}
                    </span>
                    {editaEmpresa && (
                      <button type="button" className="adm__enlace" onClick={() => setEditando(activa.nombre)}>
                        Cambiar el nombre
                      </button>
                    )}
                    {creaEmpresas && activa.activo && (
                      <button type="button" className="adm__enlace" onClick={darDeBajaLaEmpresa}>
                        Dar de baja la empresa
                      </button>
                    )}
                  </div>
                ) : (
                  <form className="adm__tarjeta-fila" onSubmit={guardarNombre}>
                    <input
                      value={editando}
                      onChange={(e) => setEditando(e.target.value)}
                      aria-label="Nombre de la empresa"
                    />
                    <button type="submit" className="adm__boton">Guardar</button>
                    <button type="button" className="adm__enlace" onClick={() => setEditando(null)}>Cancelar</button>
                  </form>
                )}

                <p className="adm__resumen">
                  {resumen.usuarios} usuario(s) · {resumen.activos} activo(s) ·
                  {" "}{resumen.administradores} administrador(es)
                  {resumen.activos > 0 && resumen.administradores === 0 && (
                    <span className="adm__alerta"> — sin administrador: nadie puede volver a dar de alta</span>
                  )}
                </p>
              </div>
            ) : (
              <p className="adm__bloqueo">No hay ninguna empresa todavía.</p>
            )}

            {/* ═══ SE MUESTRA AUNQUE NO SE PUEDA ═══

                Estaba detrás de `creaEmpresas &&` y el resultado fue el de
                siempre: quien miraba con un perfil de empresa no veía ninguna
                forma de administrar empresas y concluía —con razón— que la
                pantalla no servía para eso.

                Un control que no se puede usar y no dice por qué se lee como un
                control que no existe. Se muestra, deshabilitado, con el motivo. */}
            <form
              className="adm__alta"
              aria-label="Nueva empresa"
              onSubmit={agregarEmpresa}
              aria-disabled={!creaEmpresas}
            >
              <h4 className="adm__alta-titulo">Nueva empresa</h4>
              {!creaEmpresas && (
                <p className="adm__bloqueo">
                  Solo el administrador de plataforma crea empresas, y {nombreDePerfil(perfilQueMira).toLowerCase()}
                  {" "}no lo es. Una empresa nueva es otra instalación, con sus propios usuarios y su
                  propia operación: no es un dato que se agregue desde adentro de un cliente.
                </p>
              )}
              <input
                value={nombreNuevo}
                onChange={(e) => setNombreNuevo(e.target.value)}
                placeholder="Helicópteros del Sur S.A."
                aria-label="Nombre de la empresa nueva"
                disabled={!creaEmpresas}
              />
              <button type="submit" className="adm__boton" disabled={!creaEmpresas}>Crear</button>
            </form>
          </>
        )}
      </section>

      {/* ═══ CLIENTES ═══

          Van entre la empresa y los usuarios porque son DE la empresa. La
          pregunta «¿de quién es este vuelo?» se contesta con esta lista, y es la
          misma que el informe de horas hace para su corte por cliente. */}
      <section className="adm__seccion" aria-labelledby="adm-clientes">
        <h3 className="adm__subtitulo" id="adm-clientes">Clientes</h3>

        {!veEmpresa ? (
          <p className="adm__bloqueo">
            {nombreDePerfil(perfilQueMira)} no ve los clientes: le falta el permiso «empresa.leer».
          </p>
        ) : (
          <>
            <p className="adm__nota">
              Los clientes son <strong>de la empresa</strong>, no de la plataforma: los de{" "}
              {activa?.nombre || "esta empresa"} no se mezclan con los de otra. Es lo que el informe
              de horas usa para su corte por cliente —y por eso el nombre repetido se rechaza: dos
              renglones iguales partirían las horas del mismo cliente en dos, la mitad en cada uno,
              y ninguna de las dos filas parecería mal—. Dar de baja no borra: un cliente de baja no
              se puede elegir para un vuelo nuevo, y sus horas ya voladas siguen contando.
            </p>

            {clientesDeLaEmpresa.length === 0 ? (
              <p className="adm__bloqueo">
                {activa?.nombre || "Esta empresa"} todavía no tiene clientes cargados.
                {editaEmpresa
                  ? " Sin clientes, el corte por cliente del informe muestra un solo renglón con todo adentro."
                  : ""}
              </p>
            ) : (
              <div className="adm__tabla-caja">
                <table className="adm__tabla">
                  <caption className="adm__caption">
                    Clientes de {activa?.nombre || "—"} ·{" "}
                    {clientesDeLaEmpresa.filter((c) => c.activo).length} activo(s)
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Nombre</th>
                      <th scope="col">Contacto</th>
                      <th scope="col">Correo</th>
                      <th scope="col">Estado</th>
                      {editaEmpresa && <th scope="col">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {clientesDeLaEmpresa.map((c) => (
                      <tr key={c.id} className={c.activo ? "" : "adm__fila--baja"}>
                        <td>{c.nombre}</td>
                        <td>{c.contacto || "—"}</td>
                        <td className="adm__correo">{c.email || "—"}</td>
                        <td>{c.activo ? "activo" : "de baja"}</td>
                        {editaEmpresa && (
                          <td>
                            <button type="button" className="adm__enlace" onClick={() => alternarCliente(c)}>
                              {c.activo ? "Dar de baja" : "Reactivar"}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Se muestra aunque no se pueda, igual que el alta de empresa: un
                control que no está no dice por qué no está. */}
            <form
              className="adm__alta"
              aria-label="Nuevo cliente"
              onSubmit={agregarCliente}
              aria-disabled={!editaEmpresa}
            >
              <h4 className="adm__alta-titulo">Nuevo cliente de {activa?.nombre || "la empresa"}</h4>
              {!editaEmpresa && (
                <p className="adm__bloqueo">
                  {nombreDePerfil(perfilQueMira)} no administra el catálogo: le falta el permiso
                  «empresa.escribir». Registrar un vuelo no alcanza para dar de alta un cliente
                  —cargar el vuelo y decidir para quién se vuela son dos cosas distintas—.
                </p>
              )}
              <input
                value={clienteNuevo.nombre}
                onChange={(e) => setClienteNuevo({ ...clienteNuevo, nombre: e.target.value })}
                placeholder="Minera Andes"
                aria-label="Nombre del cliente"
                disabled={!editaEmpresa}
              />
              <input
                value={clienteNuevo.contacto}
                onChange={(e) => setClienteNuevo({ ...clienteNuevo, contacto: e.target.value })}
                placeholder="Contacto (opcional)"
                aria-label="Contacto del cliente"
                disabled={!editaEmpresa}
              />
              <input
                value={clienteNuevo.email}
                onChange={(e) => setClienteNuevo({ ...clienteNuevo, email: e.target.value })}
                placeholder="operaciones@cliente.com (opcional)"
                aria-label="Correo del cliente"
                type="email"
                disabled={!editaEmpresa}
              />
              <button type="submit" className="adm__boton" disabled={!editaEmpresa}>Crear cliente</button>
            </form>
          </>
        )}
      </section>

      {/* ═══ USUARIOS ═══ */}
      <section className="adm__seccion" aria-labelledby="adm-usuarios">
        <h3 className="adm__subtitulo" id="adm-usuarios">Usuarios</h3>

        {!veUsuarios ? (
          <p className="adm__bloqueo">
            {nombreDePerfil(perfilQueMira)} no ve los usuarios: le falta el permiso «usuarios.leer».
          </p>
        ) : (
          <>
            {/* ═══ LA VERDAD SOBRE EL MODO LOCAL ═══
                Sin API, esta pantalla guarda en el dispositivo. Un usuario
                creado así NO existe en la base y no puede entrar: el inicio de
                sesión busca en `usuarios`. Decirlo acá evita la trampa de
                crear una cuenta, cerrar sesión e intentar entrar con ella. */}
            {escribeUsuarios && !apiUsuarios && (
              <p className="adm__bloqueo" role="note">
                Esta pantalla no está conectada a la base: los usuarios que crees acá
                quedan guardados en este dispositivo y <strong>no pueden entrar</strong>.
                La administración real se hace desde Gestión, con la sesión abierta.
              </p>
            )}

            {deLaEmpresa.length === 0 ? (
              <p className="adm__bloqueo">
                Todavía no hay usuarios en {activa?.nombre || "esta empresa"}.
                {escribeUsuarios ? " Crea el primero abajo: tiene que ser un administrador, si no nadie va a poder crear a los demás." : ""}
              </p>
            ) : (
              <div className="adm__tabla-caja">
                <table className="adm__tabla">
                  <caption className="adm__caption">
                    Usuarios de {activa?.nombre || "—"}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Nombre</th>
                      <th scope="col">Correo</th>
                      <th scope="col">Perfil</th>
                      <th scope="col">Estado</th>
                      <th scope="col">Último cambio</th>
                      {escribeUsuarios && <th scope="col">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {deLaEmpresa.map((u) => {
                      const ultimo = historialDe(u)[0];
                      const soloAdministrador = esUltimoAdministradorDeEmpresa(u, usuarios);
                      return (
                        <tr key={u.id} className={u.activo ? "" : "adm__fila--baja"}>
                          <td>{u.nombre}</td>
                          <td className="adm__correo">{u.email}</td>
                          <td>
                            {escribeUsuarios ? (
                              <select
                                value={u.perfil}
                                aria-label={`Perfil de ${u.nombre}`}
                                onChange={(e) => cambiarElPerfil(u, e.target.value)}
                              >
                                {PERFILES.map((p) => (
                                  <option key={p.clave} value={p.clave}>{p.nombre}</option>
                                ))}
                              </select>
                            ) : (
                              nombreDePerfil(u.perfil)
                            )}
                            {/* El aviso va en la fila y no después de apretar:
                                si el único administrador no se puede dar de
                                baja, quien mira tiene que saberlo ANTES de
                                intentarlo. */}
                            {soloAdministrador && u.activo && (
                              <span className="adm__marca" title="Es el único administrador activo de la empresa">
                                {" "}único administrador
                              </span>
                            )}
                          </td>
                          <td>{u.activo ? "activo" : "de baja"}</td>
                          <td className="adm__cambio">
                            {ultimo ? `${ultimo.texto} · ${ultimo.el.slice(0, 10)}` : "—"}
                          </td>
                          {escribeUsuarios && (
                            <td>
                              <button type="button" className="adm__enlace" onClick={() => alternarUsuario(u)}>
                                {u.activo ? "Dar de baja" : "Reactivar"}
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {escribeUsuarios && (
              <form className="adm__alta" aria-label="Nuevo usuario" onSubmit={agregarUsuario}>
                <h4 className="adm__alta-titulo">Nuevo usuario</h4>
                <input
                  value={borrador.nombre}
                  onChange={(e) => setBorrador({ ...borrador, nombre: e.target.value })}
                  placeholder="Nombre y apellido"
                  aria-label="Nombre del usuario"
                />
                <input
                  value={borrador.email}
                  onChange={(e) => setBorrador({ ...borrador, email: e.target.value })}
                  placeholder="piloto@empresa.com"
                  aria-label="Correo del usuario"
                  type="email"
                />
                <select
                  value={borrador.perfil}
                  onChange={(e) => setBorrador({ ...borrador, perfil: e.target.value })}
                  aria-label="Perfil del usuario"
                >
                  {PERFILES.filter((p) => esDePlataforma(perfilQueMira) || !esDePlataforma(p)).map((p) => (
                    <option key={p.clave} value={p.clave}>{p.nombre}</option>
                  ))}
                </select>
                <input
                  value={borrador.clave}
                  onChange={(e) => setBorrador({ ...borrador, clave: e.target.value })}
                  placeholder="Contraseña"
                  aria-label="Contraseña del usuario"
                  type="password"
                />
                <button type="submit" className="adm__boton">Crear usuario</button>
              </form>
            )}
          </>
        )}
      </section>

      {/* ═══ PERFILES ═══ */}
      <section className="adm__seccion" aria-labelledby="adm-perfiles">
        <h3 className="adm__subtitulo" id="adm-perfiles">Perfiles</h3>

        {!vePerfiles ? (
          <p className="adm__bloqueo">
            {nombreDePerfil(perfilQueMira)} no ve la matriz: le falta el permiso «perfiles.leer».
          </p>
        ) : (
          <>
            <p className="adm__nota">
              Los perfiles son fijos de la plataforma: la empresa asigna uno, no los
              inventa. Un conjunto de perfiles que cada cliente puede crear no se puede
              auditar. La misma matriz está en <code>docs/migraciones/002_rol_permisos.sql</code>,
              y una prueba falla si las dos dejan de coincidir.
            </p>
            {/* La pregunta que sigue a la matriz es «¿y dónde cambio esto?». Si no
                se contesta acá, la matriz se lee como algo que no se puede tocar. */}
            <p className="adm__nota">
              <strong>Qué perfil tiene cada persona se cambia en Usuarios</strong>, en el
              selector de su fila. Acá se ve qué habilita cada perfil; quién lo tiene se
              administra allá.
            </p>
            <div className="adm__tabla-caja">
              <table className="adm__tabla adm__matriz">
                <caption className="adm__caption">Qué puede hacer cada perfil</caption>
                <thead>
                  <tr>
                    <th scope="col">Permiso</th>
                    {PERFILES.map((p) => (
                      <th key={p.clave} scope="col" className={p.clave === perfilQueMira ? "adm__col-actual" : ""}>
                        {p.nombre}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PERMISOS.map((permiso) => (
                    <tr key={permiso.clave}>
                      <th scope="row">
                        {permiso.nombre}
                        <code className="adm__clave">{permiso.clave}</code>
                      </th>
                      {PERFILES.map((p) => {
                        const si = puede(p.clave, permiso.clave);
                        return (
                          <td key={p.clave} className={p.clave === perfilQueMira ? "adm__col-actual" : ""}>
                            <span className={si ? "adm__si" : "adm__no"} aria-label={si ? "sí" : "no"}>
                              {si ? "✓" : "—"}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
